/**
 * Data Transformation Utilities
 * Functions for converting between different data formats and structures
 */

import {
  ITEM_TYPES,
  ITEM_STATUS,
  PAGE_STATUS,
  CHECKLIST_STATUS,
} from "../constants/itemTypes.js";

/**
 * Transforms legacy checklist data to new checklist instance format
 * @param {Object} legacyChecklist - Legacy checklist object
 * @param {Object} template - Template object to base transformation on
 * @returns {Object} New checklist instance format
 */
export const transformLegacyToInstance = (legacyChecklist, template) => {
  const now = new Date().toISOString();

  // Create a single page from legacy items
  const pageItems = (legacyChecklist.items || []).map((legacyItem, index) => {
    // Find corresponding template item or create default
    const templateItem = template.items.find(
      (ti) => ti.type === legacyItem.type
    ) || {
      id: `template_item_${index}`,
      type: legacyItem.type || ITEM_TYPES.CHECKBOX,
      required: false,
    };

    return {
      id: `item_instance_${Date.now()}_${index}_${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      templateItemId: templateItem.id,
      type: legacyItem.type || ITEM_TYPES.CHECKBOX,
      value: transformLegacyItemValue(legacyItem),
      status: legacyItem.completed
        ? ITEM_STATUS.COMPLETED
        : ITEM_STATUS.NOT_STARTED,
      completedAt: legacyItem.completed ? legacyItem.completedAt || now : null,
      createdAt: now,
      updatedAt: now,
    };
  });

  const page = {
    id: `page_${Date.now()}_1_${Math.random().toString(36).substr(2, 9)}`,
    pageNumber: 1,
    status: pageItems.every((item) => item.status === ITEM_STATUS.COMPLETED)
      ? PAGE_STATUS.COMPLETED
      : pageItems.some((item) => item.status === ITEM_STATUS.COMPLETED)
      ? PAGE_STATUS.IN_PROGRESS
      : PAGE_STATUS.NOT_STARTED,
    completedAt: pageItems.every(
      (item) => item.status === ITEM_STATUS.COMPLETED
    )
      ? now
      : null,
    items: pageItems,
    createdAt: now,
    updatedAt: now,
  };

  const completedPages = page.status === PAGE_STATUS.COMPLETED ? 1 : 0;

  return {
    id:
      legacyChecklist.id ||
      `checklist_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    templateId: template.id,
    templateVersion: template.version,
    externalReferenceId: null,
    externalAssignmentUrl: null,
    assignedTo: legacyChecklist.assignedTo || "unknown_user",
    assignedBy: null,
    assignedAt: legacyChecklist.createdAt || now,
    dueDate: legacyChecklist.dueDate || null,
    status:
      completedPages > 0 ? CHECKLIST_STATUS.COMPLETED : CHECKLIST_STATUS.DRAFT,
    totalPages: 1,
    completedPages,
    lastAccessedAt: legacyChecklist.updatedAt || now,
    pages: [page],
    completedAt: completedPages > 0 ? now : null,
    pdfGeneratedAt: null,
    pdfS3Key: null,
    pdfUrl: null,
    createdAt: legacyChecklist.createdAt || now,
    updatedAt: legacyChecklist.updatedAt || now,
  };
};

/**
 * Transforms legacy item value to new format
 * @param {Object} legacyItem - Legacy item object
 * @returns {*} Transformed item value
 */
export const transformLegacyItemValue = (legacyItem) => {
  switch (legacyItem.type) {
    case ITEM_TYPES.CHECKBOX:
      return legacyItem.completed || false;

    case ITEM_TYPES.DATE_TEXT:
      return {
        date: legacyItem.date || null,
        text: legacyItem.textValue || legacyItem.text || "",
      };

    case ITEM_TYPES.TEXT:
      return legacyItem.textValue || legacyItem.text || "";

    default:
      return legacyItem.value || null;
  }
};

/**
 * Transforms checklist instance to legacy format for backward compatibility
 * @param {Object} checklistInstance - Checklist instance object
 * @returns {Object} Legacy checklist format
 */
export const transformInstanceToLegacy = (checklistInstance) => {
  // Use first page for legacy transformation
  const firstPage = checklistInstance.pages[0];
  if (!firstPage) {
    return {
      id: checklistInstance.id,
      templateId: checklistInstance.templateId,
      items: [],
      createdAt: checklistInstance.createdAt,
      updatedAt: checklistInstance.updatedAt,
    };
  }

  const legacyItems = firstPage.items.map((item) => ({
    id: item.id,
    type: item.type,
    completed: item.status === ITEM_STATUS.COMPLETED,
    completedAt: item.completedAt,
    ...transformNewItemValueToLegacy(item),
  }));

  return {
    id: checklistInstance.id,
    templateId: checklistInstance.templateId,
    assignedTo: checklistInstance.assignedTo,
    dueDate: checklistInstance.dueDate,
    items: legacyItems,
    createdAt: checklistInstance.createdAt,
    updatedAt: checklistInstance.updatedAt,
  };
};

/**
 * Transforms new item value to legacy format
 * @param {Object} item - New item object
 * @returns {Object} Legacy item properties
 */
export const transformNewItemValueToLegacy = (item) => {
  switch (item.type) {
    case ITEM_TYPES.CHECKBOX:
      return {
        text: "", // Legacy checkbox had text field
        completed: item.value || false,
      };

    case ITEM_TYPES.DATE_TEXT:
      return {
        description: "", // Legacy had description field
        date: item.value?.date || null,
        textValue: item.value?.text || "",
        completed: item.status === ITEM_STATUS.COMPLETED,
      };

    case ITEM_TYPES.TEXT:
      return {
        text: item.value || "",
        completed: item.status === ITEM_STATUS.COMPLETED,
      };

    default:
      return {
        value: item.value,
        completed: item.status === ITEM_STATUS.COMPLETED,
      };
  }
};

/**
 * Normalizes template data to ensure consistency
 * @param {Object} template - Template object to normalize
 * @returns {Object} Normalized template
 */
export const normalizeTemplate = (template) => {
  return {
    ...template,
    items: (template.items || []).map((item, index) => ({
      id: item.id || `item_${Date.now()}_${index}`,
      type: item.type || ITEM_TYPES.CHECKBOX,
      label: item.label || "",
      required: Boolean(item.required),
      order: item.order !== undefined ? item.order : index,
      config: item.config || {},
    })),
    metadata: {
      estimatedTimeMinutes: 15,
      category: "general",
      tags: [],
      ...template.metadata,
    },
    version: template.version || "1.0.0",
    isActive: template.isActive !== undefined ? template.isActive : true,
  };
};

/**
 * Sanitizes user input data
 * @param {Object} data - Data to sanitize
 * @returns {Object} Sanitized data
 */
export const sanitizeUserInput = (data) => {
  if (typeof data !== "object" || data === null) {
    return {};
  }

  const sanitized = {};

  Object.keys(data).forEach((key) => {
    const value = data[key];

    if (typeof value === "string") {
      // Basic HTML/script sanitization
      sanitized[key] = value
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
        .replace(/<[^>]*>/g, "")
        .trim();
    } else if (typeof value === "object" && value !== null) {
      sanitized[key] = sanitizeUserInput(value);
    } else {
      sanitized[key] = value;
    }
  });

  return sanitized;
};

/**
 * Validates and transforms API response data
 * @param {Object} apiResponse - Raw API response
 * @param {string} expectedType - Expected data type ('template', 'checklist', etc.)
 * @returns {Object} Validated and transformed data
 */
export const transformApiResponse = (apiResponse, expectedType) => {
  if (!apiResponse || typeof apiResponse !== "object") {
    throw new Error("Invalid API response format");
  }

  switch (expectedType) {
    case "template":
      return normalizeTemplate(apiResponse);

    case "checklist":
      return normalizeChecklistInstance(apiResponse);

    case "templates":
      if (!Array.isArray(apiResponse)) {
        throw new Error("Expected array of templates");
      }
      return apiResponse.map((template) => normalizeTemplate(template));

    case "checklists":
      if (!Array.isArray(apiResponse)) {
        throw new Error("Expected array of checklists");
      }
      return apiResponse.map((checklist) =>
        normalizeChecklistInstance(checklist)
      );

    default:
      return apiResponse;
  }
};

/**
 * Normalizes checklist instance data
 * @param {Object} checklistInstance - Checklist instance to normalize
 * @returns {Object} Normalized checklist instance
 */
export const normalizeChecklistInstance = (checklistInstance) => {
  return {
    ...checklistInstance,
    pages: (checklistInstance.pages || []).map((page) => ({
      ...page,
      items: (page.items || []).map((item) => ({
        ...item,
        status: item.status || ITEM_STATUS.NOT_STARTED,
        value: item.value !== undefined ? item.value : null,
        createdAt: item.createdAt || new Date().toISOString(),
        updatedAt: item.updatedAt || new Date().toISOString(),
      })),
    })),
    status: checklistInstance.status || CHECKLIST_STATUS.DRAFT,
    totalPages:
      checklistInstance.totalPages || checklistInstance.pages?.length || 0,
    completedPages: checklistInstance.completedPages || 0,
  };
};

/**
 * Prepares data for API submission
 * @param {Object} data - Data to prepare for API
 * @param {string} operation - Operation type ('create', 'update', 'delete')
 * @returns {Object} API-ready data
 */
export const prepareForApi = (data, operation = "create") => {
  const prepared = { ...data };

  // Remove client-side only fields
  delete prepared._clientId;
  delete prepared._isNew;
  delete prepared._isDirty;

  // Handle timestamps based on operation
  if (operation === "create") {
    const now = new Date().toISOString();
    prepared.createdAt = now;
    prepared.updatedAt = now;
  } else if (operation === "update") {
    prepared.updatedAt = new Date().toISOString();
    delete prepared.createdAt; // Don't update creation timestamp
  }

  // Sanitize nested objects
  if (prepared.items) {
    prepared.items = prepared.items.map((item) => sanitizeUserInput(item));
  }

  if (prepared.pages) {
    prepared.pages = prepared.pages.map((page) => ({
      ...sanitizeUserInput(page),
      items: (page.items || []).map((item) => sanitizeUserInput(item)),
    }));
  }

  return prepared;
};

/**
 * Converts checklist instance to PDF data structure
 * @param {Object} checklistInstance - Checklist instance
 * @param {Object} template - Template object
 * @returns {Object} PDF data structure
 */
export const transformToPdfData = (checklistInstance, template) => {
  const pages = checklistInstance.pages.map((page) => {
    const pageItems = page.items.map((item) => {
      const templateItem =
        template.items.find((ti) => ti.id === item.templateItemId) || {};

      return {
        label: templateItem.label || "Unknown Item",
        type: item.type,
        value: formatItemValueForPdf(item.value, item.type),
        status: item.status,
        completed: item.status === ITEM_STATUS.COMPLETED,
        completedAt: item.completedAt,
      };
    });

    return {
      pageNumber: page.pageNumber,
      status: page.status,
      completedAt: page.completedAt,
      items: pageItems,
    };
  });

  return {
    checklistId: checklistInstance.id,
    templateName: template.name,
    templateVersion: checklistInstance.templateVersion,
    assignedTo: checklistInstance.assignedTo,
    assignedAt: checklistInstance.assignedAt,
    completedAt: checklistInstance.completedAt,
    totalPages: checklistInstance.totalPages,
    completedPages: checklistInstance.completedPages,
    pages,
    generatedAt: new Date().toISOString(),
  };
};

/**
 * Formats item value for PDF display
 * @param {*} value - Item value
 * @param {string} type - Item type
 * @returns {string} Formatted value for PDF
 */
export const formatItemValueForPdf = (value, type) => {
  switch (type) {
    case ITEM_TYPES.CHECKBOX:
      return value ? "✓ Yes" : "✗ No";

    case ITEM_TYPES.DATE_TEXT:
      if (value && typeof value === "object") {
        const datePart = value.date ? `Date: ${value.date}` : "";
        const textPart = value.text ? `Notes: ${value.text}` : "";
        return [datePart, textPart].filter(Boolean).join("\n");
      }
      return "No data";

    case ITEM_TYPES.TEXT:
      return value || "No text entered";

    case ITEM_TYPES.NUMBER:
      return value !== null && value !== undefined
        ? value.toString()
        : "No value";

    case ITEM_TYPES.SELECT:
      return value || "No selection";

    case ITEM_TYPES.MULTI_SELECT:
      return Array.isArray(value) ? value.join(", ") : "No selections";

    default:
      return value ? value.toString() : "No data";
  }
};

/**
 * Deep clones an object to avoid reference issues
 * @param {*} obj - Object to clone
 * @returns {*} Deep cloned object
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime());
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deepClone(item));
  }

  const cloned = {};
  Object.keys(obj).forEach((key) => {
    cloned[key] = deepClone(obj[key]);
  });

  return cloned;
};

/**
 * Merges objects deeply, with the second object taking precedence
 * @param {Object} target - Target object
 * @param {Object} source - Source object
 * @returns {Object} Merged object
 */
export const deepMerge = (target, source) => {
  const result = deepClone(target);

  Object.keys(source).forEach((key) => {
    if (
      source[key] &&
      typeof source[key] === "object" &&
      !Array.isArray(source[key])
    ) {
      if (
        result[key] &&
        typeof result[key] === "object" &&
        !Array.isArray(result[key])
      ) {
        result[key] = deepMerge(result[key], source[key]);
      } else {
        result[key] = deepClone(source[key]);
      }
    } else {
      result[key] = source[key];
    }
  });

  return result;
};

export default {
  transformLegacyToInstance,
  transformInstanceToLegacy,
  normalizeTemplate,
  sanitizeUserInput,
  transformApiResponse,
  prepareForApi,
  transformToPdfData,
  deepClone,
  deepMerge,
};
